/**
 * Super-admin editor for the full SibaScore configuration.
 *
 * Four blocks:
 *   1. Score identity — display name, trademark symbol, definition.
 *   2. Score bounds — min & max score.
 *   3. Score ranges — editable min/max/label/color table.
 *   4. 11 trust signals — reorderable list with enable, weight & description.
 *      Live sum of enabled weights is displayed; save is blocked if the total
 *      isn't exactly 100 (with ±0.5 tolerance) so the platform never ships
 *      an unbalanced score formula by accident.
 *
 * Everything writes to PUT /api/admin/sibascore/config and takes effect on
 * every SibaScore surface after the next 5-minute cache miss (or immediately
 * on the admin's browser via react-query invalidation).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Gauge, Save, Loader2, RotateCcw, Plus, Trash2,
  ArrowUp, ArrowDown, CheckCircle2, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import {
  SibaScoreConfig, SibaScoreSignal, DEFAULT_SIBASCORE_CONFIG,
} from "@/hooks/useSibaScoreRanges";

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const WEIGHT_TOLERANCE = 0.5;

// -------------------- helpers --------------------

const rowInvalid = (r: { min: any; max: any; label: string; color: string }, maxScore: number) => {
  const lo = Number(r.min);
  const hi = Number(r.max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return "min/max must be numbers";
  if (lo < 0 || hi > maxScore) return `min ≥ 0 and max ≤ ${maxScore}`;
  if (lo > hi) return "min must be ≤ max";
  if (!r.label.trim()) return "label required";
  if (!HEX_RE.test(r.color)) return "color must be #RRGGBB";
  return "";
};

const enabledWeightSum = (signals: SibaScoreSignal[]) =>
  Math.round(signals.filter((s) => s.enabled)
    .reduce((a, s) => a + (Number(s.weight) || 0), 0) * 100) / 100;

// -------------------- component --------------------

export const SibaScoreConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";
  const [form, setForm] = useState<SibaScoreConfig>(DEFAULT_SIBASCORE_CONFIG);

  const q = useQuery<SibaScoreConfig>({
    queryKey: ["admin-sibascore-config"],
    queryFn: async () => (await api.get("/admin/sibascore/config")).data,
  });
  useEffect(() => { if (q.data) setForm(q.data); }, [q.data]);

  const save = useMutation({
    mutationFn: async () => (
      await api.put("/admin/sibascore/config", {
        identity: form.identity,
        bounds: form.bounds,
        description: form.description,
        ranges: form.ranges.map((r) => ({
          min: Number(r.min), max: Number(r.max),
          label: r.label, color: r.color,
        })),
        signals: form.signals.map((s, i) => ({
          name: s.name, enabled: s.enabled,
          weight: Number(s.weight), description: s.description,
          order: i + 1,
        })),
      })
    ).data as SibaScoreConfig,
    onSuccess: (data) => {
      toast.success("SibaScore configuration saved. Live on all surfaces.");
      setForm(data);
      qc.invalidateQueries({ queryKey: ["admin-sibascore-config"] });
      qc.invalidateQueries({ queryKey: ["sibascore-config"] });
      qc.invalidateQueries({ queryKey: ["sibascore-ranges"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const reset = useMutation({
    mutationFn: async () =>
      (await api.post("/admin/sibascore/config/reset")).data as SibaScoreConfig,
    onSuccess: (d) => {
      toast.success("Restored SibaScore defaults.");
      setForm(d);
      qc.invalidateQueries({ queryKey: ["admin-sibascore-config"] });
      qc.invalidateQueries({ queryKey: ["sibascore-config"] });
      qc.invalidateQueries({ queryKey: ["sibascore-ranges"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  // ---------- identity updaters ----------
  const setIdentity = (key: "display_name" | "trademark" | "definition", v: string) =>
    setForm((f) => ({ ...f, identity: { ...f.identity, [key]: v } }));

  // ---------- bounds updaters ----------
  const setBound = (key: "min_score" | "max_score", v: number) =>
    setForm((f) => ({ ...f, bounds: { ...f.bounds, [key]: v } }));

  // ---------- range row helpers ----------
  const rangeUpd = (i: number, key: "min" | "max" | "label" | "color", v: any) =>
    setForm((f) => ({
      ...f,
      ranges: f.ranges.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)),
    }));
  const rangeAdd = () => {
    if (form.ranges.length >= 20) return;
    setForm((f) => ({
      ...f,
      ranges: [...f.ranges, { min: 0, max: 0, label: "New tier", color: "#94A3B8" }],
    }));
  };
  const rangeRemove = (i: number) => {
    if (form.ranges.length <= 1) return;
    setForm((f) => ({ ...f, ranges: f.ranges.filter((_, idx) => idx !== i) }));
  };

  // ---------- signal row helpers ----------
  const signalUpd = (i: number, key: keyof SibaScoreSignal, v: any) =>
    setForm((f) => ({
      ...f,
      signals: f.signals.map((s, idx) => (idx === i ? { ...s, [key]: v } : s)),
    }));
  const signalMove = (i: number, dir: -1 | 1) => {
    setForm((f) => {
      const arr = [...f.signals];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, signals: arr.map((s, idx) => ({ ...s, order: idx + 1 })) };
    });
  };
  const signalAdd = () => {
    if (form.signals.length >= 50) return;
    setForm((f) => ({
      ...f,
      signals: [...f.signals, {
        name: "New signal", enabled: true, weight: 0,
        description: "", order: f.signals.length + 1,
      }],
    }));
  };
  const signalRemove = (i: number) => {
    if (form.signals.length <= 1) return;
    setForm((f) => ({ ...f, signals: f.signals.filter((_, idx) => idx !== i) }));
  };

  // ---------- validation summary ----------
  const anyRangeInvalid = form.ranges.some((r) => rowInvalid(r, form.bounds.max_score) !== "");
  const identityInvalid = !form.identity.display_name.trim();
  const boundsInvalid = form.bounds.min_score >= form.bounds.max_score || form.bounds.min_score < 0;
  const sum = useMemo(() => enabledWeightSum(form.signals), [form.signals]);
  const weightsOff = Math.abs(sum - 100) > WEIGHT_TOLERANCE;
  const disableSave = save.isPending || reset.isPending
    || anyRangeInvalid || identityInvalid || boundsInvalid || weightsOff;

  // ---------- render ----------
  return (
    <div className="sg-card p-6 space-y-8" data-testid="sibascore-config-card">
      {/* HEADER */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
          <Gauge className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">
            SibaScore Configuration
          </div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5 leading-snug">
            Identity, bounds, tier ranges, and the 11 trust signals that drive
            the SibaScore. Every change here applies globally — every gauge on
            the platform picks up the new config on next fetch.
          </div>
        </div>
      </div>

      {/* IDENTITY */}
      <section data-testid="sibascore-identity-block">
        <SectionHeading title="Score identity" hint="Wherever SibaScore is rendered — headings, gauges, footers — these strings compose the branded label." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-1 block">Display name</Label>
            <Input
              data-testid="sibascore-identity-display-name"
              value={form.identity.display_name}
              maxLength={40}
              disabled={readonly}
              onChange={(e) => setIdentity("display_name", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-1 block">Trademark symbol</Label>
            <Input
              data-testid="sibascore-identity-trademark"
              value={form.identity.trademark}
              maxLength={6}
              disabled={readonly}
              onChange={(e) => setIdentity("trademark", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-1 block">Full definition</Label>
            <Input
              data-testid="sibascore-identity-definition"
              value={form.identity.definition}
              maxLength={160}
              disabled={readonly}
              onChange={(e) => setIdentity("definition", e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* BOUNDS */}
      <section data-testid="sibascore-bounds-block">
        <SectionHeading title="Score range (global)" hint="The score scale used everywhere — gauges, tiers, meters." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-md">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-1 block">Minimum score</Label>
            <Input
              data-testid="sibascore-bounds-min"
              type="number"
              value={form.bounds.min_score}
              disabled={readonly}
              onChange={(e) => setBound("min_score", Number(e.target.value))}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-1 block">Maximum score</Label>
            <Input
              data-testid="sibascore-bounds-max"
              type="number"
              value={form.bounds.max_score}
              disabled={readonly}
              onChange={(e) => setBound("max_score", Number(e.target.value))}
            />
          </div>
        </div>
        {boundsInvalid && (
          <div className="mt-2 text-[11px] text-red-600 dark:text-red-400" data-testid="sibascore-bounds-error">
            Minimum must be ≥ 0 and strictly less than maximum.
          </div>
        )}
      </section>

      {/* DESCRIPTION */}
      <section>
        <SectionHeading title="Gauge description" hint="Shown once, next to the tier badge on every gauge." />
        <Input
          data-testid="sibascore-description-input"
          value={form.description}
          maxLength={200}
          disabled={readonly}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </section>

      {/* RANGES */}
      <section data-testid="sibascore-ranges-block">
        <div className="flex items-center justify-between mb-2">
          <SectionHeading title="Score range & interpretation" hint="Tier bands that colour the gauge and label the score. Up to 20 rows." noSpacing />
          {!readonly && form.ranges.length < 20 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="sibascore-add-row"
              onClick={rangeAdd}
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" /> Add tier
            </Button>
          )}
        </div>
        <div className="hidden md:grid grid-cols-[80px_80px_1fr_150px_40px] gap-2 px-2 pb-1 text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] font-semibold">
          <div>Min</div><div>Max</div><div>Label</div><div>Color</div><div />
        </div>
        <div className="space-y-2">
          {form.ranges.map((r, i) => {
            const err = rowInvalid(r, form.bounds.max_score);
            return (
              <div
                key={i}
                data-testid={`sibascore-row-${i}`}
                className={`grid grid-cols-[80px_80px_1fr_150px_40px] items-center gap-2 rounded-lg border p-2 ${
                  err
                    ? "border-red-300 bg-red-50/40 dark:border-red-800 dark:bg-red-950/20"
                    : "border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40"
                }`}
              >
                <Input data-testid={`sibascore-row-${i}-min`} type="number" value={r.min} disabled={readonly}
                  onChange={(e) => rangeUpd(i, "min", Number(e.target.value))} className="h-9" />
                <Input data-testid={`sibascore-row-${i}-max`} type="number" value={r.max} disabled={readonly}
                  onChange={(e) => rangeUpd(i, "max", Number(e.target.value))} className="h-9" />
                <Input data-testid={`sibascore-row-${i}-label`} value={r.label} maxLength={60} disabled={readonly}
                  onChange={(e) => rangeUpd(i, "label", e.target.value)} className="h-9" />
                <div className="flex items-center gap-2">
                  <input data-testid={`sibascore-row-${i}-color-picker`} type="color"
                    value={HEX_RE.test(r.color) ? r.color : "#94A3B8"}
                    disabled={readonly}
                    onChange={(e) => rangeUpd(i, "color", e.target.value.toUpperCase())}
                    className="h-9 w-9 rounded border border-[color:var(--sg-border)] p-0 bg-transparent cursor-pointer"
                    aria-label="Pick color" />
                  <Input data-testid={`sibascore-row-${i}-color`} value={r.color} maxLength={7} disabled={readonly}
                    onChange={(e) => rangeUpd(i, "color", e.target.value)}
                    className="h-9 font-mono text-xs" placeholder="#RRGGBB" />
                </div>
                {!readonly && form.ranges.length > 1 ? (
                  <Button type="button" size="sm" variant="outline"
                    data-testid={`sibascore-row-${i}-remove`}
                    onClick={() => rangeRemove(i)}
                    className="h-9 w-9 p-0 border-red-200 text-red-700 hover:bg-red-50" title="Remove tier">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : <div />}
                {err && (
                  <div className="col-span-5 text-[11px] text-red-600 dark:text-red-400 mt-1">{err}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* SIGNALS */}
      <section data-testid="sibascore-signals-block">
        <div className="flex items-center justify-between mb-2">
          <SectionHeading title="Trust signal parameters"
            hint="The parameters that make up the score. Weights of enabled signals must sum to 100."
            noSpacing />
          <div className="flex items-center gap-2">
            <div
              data-testid="sibascore-signals-total"
              className={`text-xs font-mono font-bold px-2 py-1 rounded-md ${
                weightsOff
                  ? "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300"
                  : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
              }`}
            >
              {weightsOff
                ? <AlertCircle className="inline h-3 w-3 mr-1" />
                : <CheckCircle2 className="inline h-3 w-3 mr-1" />}
              Total: {sum}%
            </div>
            {!readonly && form.signals.length < 50 && (
              <Button type="button" size="sm" variant="outline"
                data-testid="sibascore-add-signal" onClick={signalAdd} className="h-7 text-xs">
                <Plus className="h-3 w-3 mr-1" /> Add signal
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {form.signals.map((s, i) => (
            <div key={i} data-testid={`sibascore-signal-${i}`}
              className={`rounded-lg border p-3 ${
                s.enabled
                  ? "border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40"
                  : "border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/20 opacity-70"
              }`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono text-[color:var(--sg-muted)] w-6 shrink-0">
                  #{i + 1}
                </span>
                <Input data-testid={`sibascore-signal-${i}-name`} value={s.name} maxLength={80} disabled={readonly}
                  onChange={(e) => signalUpd(i, "name", e.target.value)}
                  className="h-8 flex-1 min-w-[180px] font-semibold" />
                <div className="flex items-center gap-1.5">
                  <Switch
                    data-testid={`sibascore-signal-${i}-toggle`}
                    checked={s.enabled}
                    disabled={readonly}
                    onCheckedChange={(v) => signalUpd(i, "enabled", v)} />
                  <span className="text-[11px] text-[color:var(--sg-muted)] font-medium">
                    {s.enabled ? "On" : "Off"}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Input data-testid={`sibascore-signal-${i}-weight`} type="number" step="0.5" min="0" max="100"
                    value={s.weight} disabled={readonly || !s.enabled}
                    onChange={(e) => signalUpd(i, "weight", Number(e.target.value))}
                    className="h-8 w-20 text-right" />
                  <span className="text-[11px] text-[color:var(--sg-muted)] font-medium">%</span>
                </div>
                {!readonly && (
                  <div className="flex items-center gap-1">
                    <Button type="button" size="sm" variant="outline"
                      data-testid={`sibascore-signal-${i}-up`}
                      onClick={() => signalMove(i, -1)}
                      disabled={i === 0} className="h-8 w-8 p-0" title="Move up">
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button type="button" size="sm" variant="outline"
                      data-testid={`sibascore-signal-${i}-down`}
                      onClick={() => signalMove(i, +1)}
                      disabled={i === form.signals.length - 1} className="h-8 w-8 p-0" title="Move down">
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                    {form.signals.length > 1 && (
                      <Button type="button" size="sm" variant="outline"
                        data-testid={`sibascore-signal-${i}-remove`}
                        onClick={() => signalRemove(i)}
                        className="h-8 w-8 p-0 border-red-200 text-red-700 hover:bg-red-50" title="Remove signal">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <Input data-testid={`sibascore-signal-${i}-description`} value={s.description} maxLength={200}
                  disabled={readonly}
                  onChange={(e) => signalUpd(i, "description", e.target.value)}
                  className="h-8 text-xs" placeholder="Short description of what this signal measures" />
              </div>
            </div>
          ))}
        </div>
        {weightsOff && (
          <div className="mt-2 text-[11px] text-red-600 dark:text-red-400" data-testid="sibascore-signals-warning">
            Enabled signal weights sum to {sum}% — must equal 100% (±{WEIGHT_TOLERANCE}) before saving.
          </div>
        )}
      </section>

      {/* ACTIONS */}
      <div className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--sg-border)] pt-4">
        <div className="text-[11px] text-[color:var(--sg-muted)]">
          Applied globally to every SibaScore surface. Public cache refreshes within 5 minutes.
        </div>
        {!readonly && (
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline"
              data-testid="sibascore-reset-btn"
              disabled={reset.isPending || save.isPending}
              onClick={() => {
                if (window.confirm("Restore the shipped SibaScore defaults? Unsaved edits will be lost."))
                  reset.mutate();
              }}
              className="h-9">
              {reset.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Reset to defaults
            </Button>
            <Button type="button" size="sm"
              data-testid="sibascore-save-btn"
              disabled={disableSave}
              onClick={() => save.mutate()}
              className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white h-9">
              {save.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Save className="h-3.5 w-3.5 mr-1" />}
              Save configuration
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const SectionHeading: React.FC<{ title: string; hint?: string; noSpacing?: boolean }> = ({
  title, hint, noSpacing,
}) => (
  <div className={noSpacing ? "" : "mb-3"}>
    <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{title}</div>
    {hint && (
      <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 leading-snug">{hint}</div>
    )}
  </div>
);

// Backward-compat alias so the AdminSetup import doesn't break.
export const SibaScoreRangesCard = SibaScoreConfigCard;
export default SibaScoreConfigCard;
